/**
 * Residual-gap report — what the pack does NOT cover.
 *
 * Renders the data-layer `EvidenceResidualGapReportDto`. The probe
 * `verification-residual-gaps` asserts `scopeStatus` and `summary` against
 * evidence.get. Unknown scope must read `scope-unknown`, never "0 gaps".
 */

import { Alert, AlertDescription, AlertTitle } from "../../alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { DataTable, type DataTableColumn } from "../data-table";
import { IntentBadge } from "../status-badge";
import type {
  EvidenceResidualGapDto,
  EvidenceResidualGapReportDto,
} from "../../api/contracts/evidence";

const MUTED = "type-subtext text-muted-foreground";
const NUM = "tabular-nums";

function residualReasonIntent(
  reason: EvidenceResidualGapDto["reason"],
): "neutral" | "warning" | "danger" {
  if (reason === "not-applicable") return "neutral";
  if (reason === "judged-and-failed") return "danger";
  return "warning";
}

const COLUMNS: DataTableColumn<EvidenceResidualGapDto>[] = [
  {
    key: "predicate",
    header: "Claim",
    render: (r) => <span className="type-body">{r.predicate}</span>,
  },
  {
    key: "reason",
    header: "Reason",
    render: (r) => (
      <IntentBadge intent={residualReasonIntent(r.reason)}>{r.reason}</IntentBadge>
    ),
  },
  {
    key: "wouldDischarge",
    header: "Would discharge",
    render: (r) => <span className={MUTED}>{r.wouldDischarge}</span>,
  },
];

export function ResidualGapsCard({
  report,
  title = "Residual gaps",
}: {
  report: EvidenceResidualGapReportDto;
  title?: string;
}) {
  const statusIntent =
    report.scopeStatus === "scope-unknown"
      ? "warning"
      : report.gapCount === 0
        ? "success"
        : "danger";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-0">
        <div className="flex flex-wrap items-center gap-2 px-6 type-body">
          <IntentBadge intent={statusIntent}>{report.scopeStatus}</IntentBadge>
          <span>{report.summary}</span>
          {report.scopeCount != null ? (
            <span className={`${MUTED} ${NUM}`}>scope {report.scopeCount}</span>
          ) : null}
          <span className={`${MUTED} ${NUM}`}>discharged {report.dischargedCount}</span>
        </div>

        {report.scopeStatus === "scope-unknown" ? (
          <Alert>
            <AlertTitle>Scope cannot be enumerated</AlertTitle>
            <AlertDescription>
              This pack cannot claim 0 gaps. Discharged claims alone are not an
              enumeration of what should have been proven.
            </AlertDescription>
          </Alert>
        ) : report.gaps.length === 0 ? (
          <p className="type-body text-muted-foreground">
            0 gaps — every enumerated in-scope claim was discharged.
          </p>
        ) : (
          <DataTable
            rows={report.gaps}
            columns={COLUMNS}
            rowKey={(r) => r.id}
            wrapInCard={false}
          />
        )}
      </CardContent>
    </Card>
  );
}
